import { Link } from "react-router-dom";
import { Check, Phone, MessageCircle, ArrowRight, Mail, ShieldCheck, FileText, Calculator, Eye, AlertTriangle, Headphones, Sparkles, ClipboardList } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { trackEvent, EVENTS } from "../lib/analytics";
import SEO, { ORG_JSONLD, breadcrumb, serviceJsonLd } from "../components/SEO";

const WHATSAPP_MSG = WA_MSG.premium;
const MAIL_URL = "mailto:fmsubastasinvestment@gmail.com?subject=Informe%20Premium%20de%20Cargas%20y%20Deudas";

const INCLUDES = [
  { icon: FileText, title: "Listado completo de cargas registrales",
    desc: "Te mostramos todas las cargas inscritas en el Registro de la Propiedad, detallando cuáles se eliminan con la adjudicación y cuáles siguen vigentes — es decir, las que pasan a ser tu responsabilidad si resultas adjudicatario." },
  { icon: Eye, title: "Análisis de posibles deudas NO registradas",
    desc: "Las trampas que no se ven a simple vista: deudas de comunidad, recibos impagados, IBI acumulado… Aunque no estén registradas, existen. Te orientamos para detectarlas y evitarlas." },
  { icon: Calculator, title: "Valor de mercado actualizado del inmueble",
    desc: "Una estimación realista del valor de mercado para que pujes con estrategia y no con el corazón." },
  { icon: Calculator, title: "Cálculo del ITP (Impuesto de Transmisiones Patrimoniales)",
    desc: "Estimamos con antelación lo que pagarás en concepto de ITP en base al valor catastral de referencia. Para que no te lleves un sablazo fiscal inesperado después de ganar." },
  { icon: ShieldCheck, title: "Recomendación de puja máxima sugerida",
    desc: "Analizamos cargas, valor e impuestos y te damos una referencia clara de puja máxima rentable. En una subasta, saber hasta dónde llegar puede ser más valioso que ganar." },
  { icon: AlertTriangle, title: "Advertencias y observaciones clave",
    desc: "Si hay incoherencias, riesgos o detalles preocupantes, te los señalamos sin rodeos. Preferimos que no pujes antes que te arrepientas después." },
  { icon: Headphones, title: "Acompañamiento telemático el día de la subasta",
    desc: "El día D estamos contigo en tiempo real: resolvemos dudas, te recordamos pasos clave y te ayudamos a ejecutar la puja correctamente. No estás solo." },
  { icon: ShieldCheck, title: "Asesoramiento jurídico en cada fase posterior",
    desc: "Una vez adjudicado el inmueble, te acompañamos jurídicamente en todas las fases que vienen después: comunicaciones con el juzgado, plazos procesales, comparecencias y cualquier incidencia legal que pueda surgir hasta tener la propiedad correctamente inscrita." },
  { icon: FileText, title: "Redacción de escritos y comunicaciones legales",
    desc: "Preparamos los escritos necesarios para presentar ante juzgado, administraciones y Registro: solicitudes de mandamiento, recursos, alegaciones, comunicaciones con la entidad gestora. Todo con respaldo jurídico profesional." },
  { icon: ClipboardList, title: "Planificación post-adjudicación",
    desc: "Hoja de ruta clara con los pasos a seguir tras la adjudicación: liquidación fiscal, gestión registral, posesión, plazos críticos y posibles contingencias. Sabrás qué viene, cuándo y cómo prepararte." },
  { icon: ShieldCheck, title: "Consejos legales personalizados",
    desc: "Recomendaciones jurídicas adaptadas a tu situación concreta: estrategia frente a ocupación, gestión de deudas no registrales, defensa frente a terceros, fiscalidad futura del inmueble. Asesoría real, no respuestas genéricas." },
];

export default function InformePremium() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Informe Premium de Cargas y Deudas · 335 € (bonificado 135 €)"
        description="Informe completo de cargas, valor de mercado, cálculo del ITP, puja máxima recomendada y acompañamiento el día de la subasta. Asesoramiento jurídico hasta inscripción registral."
        keywords="informe completo subasta, valor de mercado subasta, ITP impuesto transmisiones, puja máxima subasta, asesoramiento jurídico subasta inmobiliaria"
        path="/informe-premium"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }, { name: "Informe Premium", path: "/informe-premium" }]),
          serviceJsonLd({
            name: "Informe Premium de Cargas y Deudas",
            description: "Informe completo de cargas registrales, valoración de mercado, ITP, recomendación de puja y acompañamiento integral antes, durante y después de la subasta inmobiliaria.",
            price: 335,
            path: "/informe-premium",
          }),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] px-3 py-1 rounded-sm text-[10px] tracking-[0.25em] uppercase font-bold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Servicio integral · Tu escudo en la subasta
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              Informe de Cargas y Deudas:<br /><span className="text-[#E0651F]">tu escudo</span> en la subasta.
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed max-w-2xl mb-3 font-display font-medium">
              Participar puede ser una gran oportunidad… o una trampa elegante con alfombra roja.
            </p>
            <p className="text-sm text-stone-400 leading-relaxed max-w-2xl mb-8">
              Por eso creamos este servicio esencial: una herramienta precisa, clara y pensada para protegerte desde el primer clic hasta el último segundo de la puja. Cargas, deudas ocultas, ITP, valor de mercado, puja máxima y acompañamiento el día D.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/informe-premium/solicitar"
                onClick={() => trackEvent(EVENTS.CTA_INFORME_PREMIUM, { source: "informe_premium_hero" })}
                className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-solicitar">
                Solicitar informe ahora <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "informe_premium_hero" }); openWhatsApp(WHATSAPP_MSG); }}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={MAIL_URL}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-mail">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-[#2d1f1a] p-8 rounded-sm border-l-4 border-[#E0651F] shadow-2xl">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Tarifa</div>
              <div className="mb-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-black">335 €</span>
                  <span className="text-sm text-stone-500">IVA incluido</span>
                </div>
                <div className="text-xs text-stone-500 mt-1">Precio estándar del informe</div>
              </div>
              <div className="my-5 border-t border-stone-200" />
              <div className="bg-[#E0651F]/10 border border-[#E0651F]/30 p-4 rounded-sm">
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-1">Precio bonificado</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-black text-[#E0651F]">135 €</span>
                  <span className="text-sm text-stone-600">IVA incluido</span>
                </div>
                <div className="text-xs text-stone-700 mt-1 leading-relaxed">
                  Condicionado a contratar también la <Link to="/gestion-postsubasta" className="font-bold text-[#E0651F] hover:underline" data-testid="link-gestion-postsubasta">Gestión Completa post-subasta</Link>. Nuestra forma de premiar una experiencia profesional integral.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Qué incluye el informe</div>
          <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-4">Una radiografía completa antes de pujar.</h2>
          <p className="text-stone-600 text-base leading-relaxed">Análisis registral completo, valoración económica, fiscalidad, recomendación de puja, acompañamiento el día de la subasta y asesoramiento jurídico integral hasta tener tu inmueble correctamente inscrito.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
          {INCLUDES.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2d1f1a] flex items-center justify-center rounded-sm">
                  <Icon className="w-5 h-5 text-[#E0651F]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E0651F] mb-1">{String(i + 1).padStart(2, "0")} · Incluido</div>
                  <h3 className="font-display font-black text-lg text-[#2d1f1a] tracking-tight mb-2">{it.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{it.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* POLITICA DESISTIMIENTO */}
      <section className="bg-stone-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border-l-4 border-[#E0651F] p-8 sm:p-10 rounded-sm shadow-sm">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-7 h-7 text-[#E0651F] flex-shrink-0 mt-1" />
              <div className="min-w-0">
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Transparencia total · Cómo funciona el precio</div>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-[#2d1f1a] tracking-tight mb-5 leading-tight" data-testid="informe-bonificado-title">
                  Informe Bonificado y Gestión Post-subasta
                </h2>

                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  El <strong>Informe Bonificado</strong> tiene un precio de <strong className="text-[#2d1f1a]">135 € IVA incluido</strong> y se contrata desde el inicio, mediante la firma del contrato correspondiente y el pago del servicio.
                </p>
                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  Esta modalidad bonificada está vinculada <strong>únicamente a la subasta concreta</strong> sobre la que se solicita el informe.
                </p>
                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  Al contratar el Informe Bonificado, el cliente acepta desde ese momento que, si resulta adjudicatario de esa subasta, <strong>Fernando Marcos Subastas &amp; Investment</strong> se encargará de la <strong>Gestión Completa post-subasta</strong>, incluyendo los trámites posteriores derivados de la adjudicación: liquidación de impuestos, inscripción registral, plusvalía municipal, solicitud de posesión y demás gestiones necesarias.
                </p>
                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  La decisión entre contratar el informe como <strong>servicio independiente</strong> o acogerse al <strong>Informe Bonificado</strong> debe estar tomada en el momento de la contratación y pago del servicio, antes de la elaboración y entrega del informe. <strong>No se trata de una decisión posterior al resultado de la subasta.</strong>
                </p>

                <div className="grid md:grid-cols-3 gap-4 my-8">
                  <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm" data-testid="escenario-bonificado">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700 mb-2">Escenario 1 · Bonificado</div>
                    <div className="font-display text-2xl font-black text-emerald-700">135 €</div>
                    <p className="text-xs text-stone-700 mt-2 leading-relaxed">
                      Informe + compromiso de <strong>Gestión Completa post-subasta</strong> si resultas adjudicatario. Se contrata antes del informe.
                    </p>
                  </div>
                  <div className="bg-stone-50 border border-stone-300 p-4 rounded-sm" data-testid="escenario-estandar">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-600 mb-2">Escenario 2 · Estándar</div>
                    <div className="font-display text-2xl font-black text-[#2d1f1a]">335 €</div>
                    <p className="text-xs text-stone-700 mt-2 leading-relaxed">
                      Solo informe Premium, <strong>sin compromiso</strong> de gestión posterior. Recibes la radiografía completa y el acompañamiento el día D.
                    </p>
                  </div>
                  <div className="bg-[#2d1f1a] text-white p-4 rounded-sm" data-testid="escenario-no-ganas">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#E0651F] mb-2">Escenario 3 · No ganas</div>
                    <div className="font-display text-2xl font-black text-[#E0651F]">0 € extra</div>
                    <p className="text-xs text-stone-300 mt-2 leading-relaxed">
                      Si contrataste el bonificado pero <strong>no resultas adjudicatario</strong>, no hay gestión post-subasta que activar y <strong>no se factura ningún importe adicional</strong>. Solo abonas los 135 € del informe.
                    </p>
                  </div>
                </div>

                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  Si el cliente <strong>no resulta adjudicatario</strong>, no habrá gestión post-subasta que realizar, no se activará ningún servicio posterior y no se facturará ningún importe adicional. En ese caso, el único importe abonado será el del Informe Bonificado: <strong className="text-[#2d1f1a]">135 € IVA incluido</strong>.
                </p>
                <p className="text-sm text-stone-700 leading-relaxed mb-4">
                  La única vinculación derivada del Informe Bonificado se produce, por tanto, <strong>si el cliente resulta adjudicatario</strong> de la subasta analizada en el informe. En ese supuesto se activa la Gestión Completa post-subasta conforme a las condiciones pactadas en el contrato.
                </p>
                <div className="bg-[#E0651F]/10 border-l-4 border-[#E0651F] px-4 py-3 rounded-sm">
                  <p className="text-sm text-[#2d1f1a] leading-relaxed">
                    <strong>Importante:</strong> el precio bonificado del informe <strong>no implica ningún coste adicional si no se gana la subasta</strong>. Solo en caso de adjudicación se activa la Gestión Completa post-subasta, al existir trámites posteriores que gestionar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">¿Por qué es tan importante?</div>
        <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-6 max-w-3xl leading-tight">
          En el mundo de las subastas, la ignorancia no exime… <span className="text-[#E0651F]">se paga con intereses.</span>
        </h2>
        <p className="text-stone-600 text-base leading-relaxed max-w-3xl">
          Con este informe tendrás una radiografía completa del inmueble antes de lanzarte a pujar. Y si decides que te acompañemos hasta el final, lo haremos con todo el rigor legal y humano que exige una inversión de este nivel.
        </p>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-[#2d1f1a] text-white py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-display text-3xl font-black tracking-tight leading-tight mb-4">
            Asegura tu informe y da el primer paso<br />hacia una subasta sin sorpresas.
          </h3>
          <p className="text-stone-300 mb-8 max-w-xl mx-auto leading-relaxed">
            Como cada caso es distinto, los pagos del informe Premium se concretan directamente con un agente. Contáctanos por el canal que prefieras y te enviamos condiciones, factura y siguientes pasos.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/informe-premium/solicitar"
              onClick={() => trackEvent(EVENTS.CTA_INFORME_PREMIUM, { source: "informe_premium_footer" })}
              className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
              data-testid="footer-cta-solicitar">
              Solicitar informe ahora <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.CTA_WHATSAPP, { source: "informe_premium_footer" }); openWhatsApp(WHATSAPP_MSG); }}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
              data-testid="footer-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href="tel:+34695536486"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              <Phone className="w-4 h-4" /> +34 695 536 486
            </a>
            <a href={MAIL_URL}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              <Mail className="w-4 h-4" /> Email
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
